import { supabase } from './supabase';

export interface LabAnalyzerImport {
  id: string;
  analyzer_name?: string;
  file_name?: string;
  file_url?: string;
  imported_by?: string;
  imported_at: string;
  status: 'pending' | 'processed' | 'failed';
  total_rows: number;
  processed_rows: number;
  remarks?: string;
  created_at: string;
}

export interface LabReagent {
  id: string;
  name: string;
  lot_no?: string;
  quantity: number;
  unit: string;
  reorder_level: number;
  expiry?: string;
  last_updated: string;
  created_at: string;
}

export interface LabReagentUsage {
  id: string;
  reagent_id: string;
  test_id?: string;
  used_on: string;
  used_by?: string;
  quantity_used: number;
  created_at: string;
  reagent?: LabReagent;
}

export interface TATSummary {
  department: string;
  test_name: string;
  test_code: string;
  total_orders: number;
  avg_tat_minutes: number;
  min_tat_minutes: number;
  max_tat_minutes: number;
  stat_count: number;
  urgent_count: number;
  routine_count: number;
}

export const analyzerImportService = {
  async getAll(): Promise<LabAnalyzerImport[]> {
    const { data, error } = await supabase
      .from('lab_analyzer_imports')
      .select('*')
      .order('imported_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<LabAnalyzerImport | null> {
    const { data, error } = await supabase
      .from('lab_analyzer_imports')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(importData: Partial<LabAnalyzerImport>): Promise<LabAnalyzerImport> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('lab_analyzer_imports')
      .insert({
        ...importData,
        imported_by: userData?.user?.id,
        imported_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateStatus(
    id: string,
    status: 'pending' | 'processed' | 'failed',
    processedRows?: number,
    remarks?: string
  ): Promise<LabAnalyzerImport> {
    const { data, error } = await supabase
      .from('lab_analyzer_imports')
      .update({
        status,
        processed_rows: processedRows,
        remarks,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async uploadFile(file: File): Promise<string> {
    const fileName = `analyzer-imports/${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage
      .from('lab-reports')
      .upload(fileName, file);

    if (error) throw error;
    
    const { data: urlData } = await supabase.storage
      .from('lab-reports')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  },

  async processImport(importId: string, rows: any[]): Promise<{
    matched: number;
    updated: number;
    failed: number;
  }> {
    let matched = 0;
    let updated = 0;
    let failed = 0;

    for (const row of rows) {
      try {
        const { barcode, test_code, value, unit, flag } = row;

        const { data: sample } = await supabase
          .from('lab_samples')
          .select('order_id')
          .eq('barcode', barcode)
          .single();

        if (!sample) {
          failed++;
          continue;
        }

        matched++;

        const { data: test } = await supabase
          .from('lab_tests')
          .select('id')
          .eq('code', test_code)
          .single();

        if (!test) {
          failed++;
          continue;
        }

        const { data: orderItem } = await supabase
          .from('lab_order_items')
          .select('id')
          .eq('order_id', sample.order_id)
          .eq('test_id', test.id)
          .single();

        if (!orderItem) {
          failed++;
          continue;
        }

        const { data: result } = await supabase
          .from('lab_results')
          .select('id')
          .eq('order_item_id', orderItem.id)
          .maybeSingle();

        if (result) {
          await supabase
            .from('lab_results')
            .update({
              value,
              unit,
              flag,
            })
            .eq('id', result.id);
        } else {
          await supabase
            .from('lab_results')
            .insert({
              order_item_id: orderItem.id,
              analyte: test_code,
              value,
              unit,
              flag,
            });
        }

        updated++;
      } catch (error) {
        console.error('Error processing row:', error);
        failed++;
      }
    }

    return { matched, updated, failed };
  },
};

export const reagentService = {
  async getAll(): Promise<LabReagent[]> {
    const { data, error } = await supabase
      .from('lab_reagents')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<LabReagent | null> {
    const { data, error } = await supabase
      .from('lab_reagents')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(reagent: Partial<LabReagent>): Promise<LabReagent> {
    const { data, error } = await supabase
      .from('lab_reagents')
      .insert(reagent)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, reagent: Partial<LabReagent>): Promise<LabReagent> {
    const { data, error } = await supabase
      .from('lab_reagents')
      .update({
        ...reagent,
        last_updated: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('lab_reagents')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getLowStock(): Promise<LabReagent[]> {
    const { data, error } = await supabase
      .from('lab_reagents')
      .select('*')
      .or('quantity.lte.reorder_level')
      .order('quantity');

    if (error) throw error;
    return data || [];
  },

  async getExpiringSoon(days: number = 30): Promise<LabReagent[]> {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    const { data, error } = await supabase
      .from('lab_reagents')
      .select('*')
      .lte('expiry', futureDate.toISOString().split('T')[0])
      .order('expiry');

    if (error) throw error;
    return data || [];
  },
};

export const reagentUsageService = {
  async getByReagent(reagentId: string): Promise<LabReagentUsage[]> {
    const { data, error } = await supabase
      .from('lab_reagent_usage')
      .select('*, reagent:lab_reagents(*)')
      .eq('reagent_id', reagentId)
      .order('used_on', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usage: Partial<LabReagentUsage>): Promise<LabReagentUsage> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('lab_reagent_usage')
      .insert({
        ...usage,
        used_by: userData?.user?.id,
        used_on: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async logUsageForTest(testId: string, reagentId: string, quantity: number): Promise<void> {
    await this.create({
      test_id: testId,
      reagent_id: reagentId,
      quantity_used: quantity,
    });
  },
};

export const tatService = {
  async getSummary(filters?: {
    department?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<TATSummary[]> {
    let query = supabase
      .from('lab_tat_summary')
      .select('*')
      .order('avg_tat_minutes', { ascending: false });

    if (filters?.department) {
      query = query.eq('department', filters.department);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async getOrdersWithTAT(filters?: {
    priority?: string;
    overdue?: boolean;
    startDate?: string;
    endDate?: string;
  }): Promise<any[]> {
    let query = supabase
      .from('lab_orders')
      .select(`
        *,
        patient:patients(id, name),
        items:lab_order_items(
          *,
          test:lab_tests(code, name, tat_hours)
        )
      `)
      .not('tat_minutes', 'is', null)
      .order('created_at', { ascending: false });

    if (filters?.priority) {
      query = query.eq('priority', filters.priority);
    }

    if (filters?.overdue) {
      query = query.lt('due_at', new Date().toISOString());
    }

    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },
};
